import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Video, VideoOff, Mic, MicOff, PhoneOff, 
  Monitor, MonitorOff, Maximize2, Minimize2, X,
  MessageSquare, Users, PictureInPicture2,
  Send, Loader2, Sparkles, Smile, Image as ImageIcon,
  Palette, PenTool, FileText, Pin, Clock,
  Coffee, Brain, Clock3, ChevronRight, ChevronDown,
  Eraser, Square, Circle as CircleIcon, Type, Undo, Trash2,
  Play, Pause, Volume2, Download
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Premium Color Palette - Creative Agency Style
const COLORS = {
  // Frosted glass colors
  glass: 'rgba(255, 255, 255, 0.08)',
  glassBorder: 'rgba(255, 255, 255, 0.12)',
  glassHover: 'rgba(255, 255, 255, 0.15)',
  
  // Vibrant gradients
  purple: '#8B5CF6',
  purpleGlow: 'rgba(139, 92, 246, 0.4)',
  teal: '#14B8A6',
  tealGlow: 'rgba(20, 184, 166, 0.4)',
  green: '#10B981',
  greenGlow: 'rgba(16, 185, 129, 0.4)',
  blue: '#3B82F6',
  blueGlow: 'rgba(59, 130, 246, 0.4)',
  pink: '#EC4899',
  pinkGlow: 'rgba(236, 72, 153, 0.4)',
  
  // UI colors
  danger: '#EF4444',
  dangerGlow: 'rgba(239, 68, 68, 0.4)',
  surface: '#0F0F1A',
  surfaceLight: '#1A1A2E',
  text: '#F8FAFC',
  textMuted: '#94A3B8',
  
  // Avatar gradients
  avatarGradients: [
    'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
    'linear-gradient(135deg, #14B8A6 0%, #3B82F6 100%)',
    'linear-gradient(135deg, #10B981 0%, #14B8A6 100%)',
    'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
    'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
  ]
};

// Background presets
const BACKGROUND_PRESETS = [
  { id: 'blur', name: 'Blur', type: 'blur' },
  { id: 'gradient1', name: 'Aurora', gradient: 'linear-gradient(135deg, #0F0F1A 0%, #1A1A2E 50%, #2D1B4E 100%)' },
  { id: 'gradient2', name: 'Ocean', gradient: 'linear-gradient(135deg, #0F172A 0%, #0E4D64 50%, #0D9488 100%)' },
  { id: 'gradient3', name: 'Sunset', gradient: 'linear-gradient(135deg, #1F1144 0%, #5B2C6F 50%, #EC4899 100%)' },
  { id: 'gradient4', name: 'Forest', gradient: 'linear-gradient(135deg, #0A1F1A 0%, #134E4A 50%, #10B981 100%)' },
];

// Reaction emojis
const REACTIONS = ['👏', '❤️', '🔥', '😂', '🎉', '💡', '👍', '✨'];

// Status options
const STATUS_OPTIONS = [
  { id: 'none', label: 'Clear Status', icon: null },
  { id: 'focused', label: 'Focused', icon: Brain, color: COLORS.purple },
  { id: 'thinking', label: 'Thinking', icon: Clock3, color: COLORS.teal },
  { id: 'brb', label: 'Be Right Back', icon: Coffee, color: COLORS.pink },
];

// WebRTC Configuration
const RTC_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]
};

// ============================================
// FLOATING REACTIONS COMPONENT
// ============================================
const FloatingReaction = ({ emoji, onComplete }) => {
  const [style, setStyle] = useState({
    left: `${20 + Math.random() * 60}%`,
    bottom: '100px',
    opacity: 1,
    transform: 'scale(1)',
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setStyle(prev => ({
        ...prev,
        bottom: '80%',
        opacity: 0,
        transform: 'scale(1.5)',
      }));
    }, 50);

    const removeTimer = setTimeout(onComplete, 3000);
    return () => {
      clearTimeout(timer);
      clearTimeout(removeTimer);
    };
  }, [onComplete]);

  return (
    <div
      className="absolute text-4xl pointer-events-none transition-all duration-3000 ease-out"
      style={style}
    >
      {emoji}
    </div>
  );
};

// ============================================
// WHITEBOARD COMPONENT
// ============================================
const Whiteboard = ({ onClose }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#8B5CF6');
  const [brushSize, setBrushSize] = useState(3);
  const [history, setHistory] = useState([]);
  const lastPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1A1A2E';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    lastPos.current = { x, y };
    setIsDrawing(true);
    
    // Save state for undo
    const ctx = canvas.getContext('2d');
    setHistory(prev => [...prev, ctx.getImageData(0, 0, canvas.width, canvas.height)]);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    
    if (tool === 'eraser') {
      ctx.strokeStyle = '#1A1A2E';
      ctx.lineWidth = brushSize * 3;
    } else {
      ctx.strokeStyle = color;
      ctx.lineWidth = brushSize;
    }
    
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();

    lastPos.current = { x, y };
  };

  const stopDrawing = () => setIsDrawing(false);

  const undo = () => {
    if (history.length === 0) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const lastState = history[history.length - 1];
    ctx.putImageData(lastState, 0, 0);
    setHistory(prev => prev.slice(0, -1));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    setHistory(prev => [...prev, ctx.getImageData(0, 0, canvas.width, canvas.height)]);
    ctx.fillStyle = '#1A1A2E';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const tools = [
    { id: 'pen', icon: PenTool, label: 'Pen' },
    { id: 'eraser', icon: Eraser, label: 'Eraser' },
  ];

  const colors = ['#8B5CF6', '#14B8A6', '#10B981', '#3B82F6', '#EC4899', '#F59E0B', '#FFFFFF'];

  return (
    <div className="absolute inset-0 z-50 flex flex-col" style={{ background: 'rgba(15, 15, 26, 0.95)' }}>
      {/* Toolbar */}
      <div 
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ 
          background: COLORS.glass, 
          borderColor: COLORS.glassBorder,
          backdropFilter: 'blur(20px)'
        }}
      >
        <div className="flex items-center gap-4">
          <span className="text-white font-medium flex items-center gap-2">
            <PenTool className="w-4 h-4" style={{ color: COLORS.purple }} />
            Whiteboard
          </span>
          
          {/* Tools */}
          <div className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: COLORS.glass }}>
            {tools.map(t => (
              <button
                key={t.id}
                onClick={() => setTool(t.id)}
                className={`p-2 rounded-lg transition-all ${tool === t.id ? 'bg-white/20' : 'hover:bg-white/10'}`}
                title={t.label}
              >
                <t.icon className="w-4 h-4 text-white" />
              </button>
            ))}
          </div>

          {/* Colors */}
          <div className="flex items-center gap-1">
            {colors.map(c => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-6 h-6 rounded-full transition-all ${color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-transparent' : ''}`}
                style={{ background: c }}
              />
            ))}
          </div>

          {/* Brush Size */}
          <input
            type="range"
            min="1"
            max="20"
            value={brushSize}
            onChange={(e) => setBrushSize(parseInt(e.target.value))}
            className="w-20"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={undo} className="text-white hover:bg-white/10">
            <Undo className="w-4 h-4 mr-1" /> Undo
          </Button>
          <Button variant="ghost" size="sm" onClick={clear} className="text-white hover:bg-white/10">
            <Trash2 className="w-4 h-4 mr-1" /> Clear
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/10">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={1200}
        height={700}
        className="flex-1 cursor-crosshair mx-auto my-4 rounded-xl"
        style={{ maxWidth: '100%', maxHeight: 'calc(100% - 80px)' }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
      />
    </div>
  );
};

// ============================================
// TRANSCRIPTION PANEL COMPONENT
// ============================================
const TranscriptionPanel = ({ transcripts, onPin, onClose }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcripts]);

  return (
    <div 
      className="absolute right-0 top-0 bottom-0 w-96 flex flex-col border-l z-40"
      style={{ 
        background: 'linear-gradient(180deg, rgba(15, 15, 26, 0.98) 0%, rgba(26, 26, 46, 0.98) 100%)',
        borderColor: COLORS.glassBorder,
        backdropFilter: 'blur(20px)'
      }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: COLORS.glassBorder }}
      >
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-white text-sm font-medium">Live Transcription</span>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="text-white/60 hover:text-white h-7 w-7 p-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Transcripts */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {transcripts.map((t, idx) => (
          <div 
            key={idx} 
            className="group relative p-3 rounded-xl transition-all hover:bg-white/5"
            style={{ background: t.pinned ? 'rgba(139, 92, 246, 0.1)' : 'transparent' }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium" style={{ color: COLORS.purple }}>
                  {t.speaker}
                </span>
                <span className="text-xs" style={{ color: COLORS.textMuted }}>
                  {t.timestamp}
                </span>
              </div>
              <button
                onClick={() => onPin(idx)}
                className={`opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${t.pinned ? 'text-purple-400' : 'text-white/40 hover:text-white'}`}
              >
                <Pin className="w-3 h-3" />
              </button>
            </div>
            <p className="text-white/80 text-sm leading-relaxed">{t.text}</p>
          </div>
        ))}
        {transcripts.length === 0 && (
          <div className="text-center py-8">
            <FileText className="w-8 h-8 mx-auto mb-2" style={{ color: COLORS.textMuted }} />
            <p className="text-sm" style={{ color: COLORS.textMuted }}>
              Transcription will appear here...
            </p>
          </div>
        )}
      </div>

      {/* AI Badge */}
      <div 
        className="px-4 py-3 border-t"
        style={{ borderColor: COLORS.glassBorder }}
      >
        <div className="flex items-center gap-2 text-xs" style={{ color: COLORS.textMuted }}>
          <Sparkles className="w-3 h-3" style={{ color: COLORS.purple }} />
          Powered by AI • Auto-transcribing
        </div>
      </div>
    </div>
  );
};

// ============================================
// MEETING SUMMARY MODAL
// ============================================
const MeetingSummaryModal = ({ summary, onClose, onPostToThread }) => {
  if (!summary) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div 
        className="w-full max-w-2xl max-h-[80vh] overflow-hidden rounded-3xl"
        style={{ 
          background: 'linear-gradient(180deg, #1A1A2E 0%, #0F0F1A 100%)',
          border: `1px solid ${COLORS.glassBorder}`,
          boxShadow: '0 25px 100px -20px rgba(139, 92, 246, 0.3)'
        }}
      >
        {/* Header */}
        <div 
          className="px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: COLORS.glassBorder }}
        >
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.pink} 100%)` }}
            >
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-semibold">Meeting Summary</h2>
              <p className="text-xs" style={{ color: COLORS.textMuted }}>
                {summary.duration} • {summary.participants?.length || 0} participants
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-6">
          {/* Key Points */}
          <div>
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: COLORS.purple }} />
              Key Discussion Points
            </h3>
            <ul className="space-y-2">
              {summary.keyPoints?.map((point, idx) => (
                <li key={idx} className="text-white/70 text-sm pl-4 border-l-2" style={{ borderColor: COLORS.purple }}>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* Decisions */}
          {summary.decisions?.length > 0 && (
            <div>
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: COLORS.teal }} />
                Decisions Made
              </h3>
              <ul className="space-y-2">
                {summary.decisions.map((decision, idx) => (
                  <li key={idx} className="text-white/70 text-sm pl-4 border-l-2" style={{ borderColor: COLORS.teal }}>
                    {decision}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Items */}
          {summary.actionItems?.length > 0 && (
            <div>
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: COLORS.green }} />
                Action Items
              </h3>
              <ul className="space-y-2">
                {summary.actionItems.map((item, idx) => (
                  <li 
                    key={idx} 
                    className="flex items-start gap-3 p-3 rounded-xl"
                    style={{ background: COLORS.glass }}
                  >
                    <div className="w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5" style={{ borderColor: COLORS.green }} />
                    <div>
                      <p className="text-white/80 text-sm">{item.task}</p>
                      {item.assignee && (
                        <p className="text-xs mt-1" style={{ color: COLORS.textMuted }}>
                          Assigned to: {item.assignee}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div 
          className="px-6 py-4 border-t flex items-center justify-between"
          style={{ borderColor: COLORS.glassBorder }}
        >
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white">
            Close
          </Button>
          <Button 
            onClick={onPostToThread}
            className="gap-2"
            style={{ 
              background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.pink} 100%)`,
              border: 'none'
            }}
          >
            <Send className="w-4 h-4" />
            Post to Thread
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// PARTICIPANT TILE COMPONENT
// ============================================
const ParticipantTile = ({ 
  participant, 
  stream, 
  isLocal, 
  isSpeaking, 
  speakingTime,
  isLarge,
  avatarGradient 
}) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const StatusIcon = STATUS_OPTIONS.find(s => s.id === participant.status)?.icon;

  return (
    <div 
      className={`relative rounded-2xl overflow-hidden transition-all duration-500 ${isLarge ? 'col-span-2 row-span-2' : ''}`}
      style={{ 
        background: participant.videoEnabled 
          ? 'transparent' 
          : avatarGradient || COLORS.avatarGradients[0],
        boxShadow: isSpeaking 
          ? `0 0 0 3px ${COLORS.purple}, 0 0 30px ${COLORS.purpleGlow}` 
          : '0 8px 32px rgba(0,0,0,0.3)',
        aspectRatio: isLarge ? 'auto' : '16/9'
      }}
    >
      {/* Video */}
      {participant.videoEnabled && stream ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className="w-full h-full object-cover"
        />
      ) : (
        /* Avatar when video off */
        <div className="absolute inset-0 flex items-center justify-center">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-2xl"
            style={{ 
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.2)'
            }}
          >
            {participant.avatar || participant.name?.slice(0, 2) || '??'}
          </div>
        </div>
      )}

      {/* Speaking indicator ring */}
      {isSpeaking && (
        <div 
          className="absolute inset-0 rounded-2xl pointer-events-none animate-pulse"
          style={{ 
            boxShadow: `inset 0 0 0 2px ${COLORS.purple}`,
            background: `linear-gradient(135deg, ${COLORS.purpleGlow} 0%, transparent 50%)`
          }}
        />
      )}

      {/* Name & Status Badge */}
      <div 
        className="absolute bottom-3 left-3 right-3 flex items-center justify-between"
      >
        <div 
          className="flex items-center gap-2 px-3 py-1.5 rounded-full"
          style={{ 
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <span className="text-white text-xs font-medium">
            {isLocal ? 'You' : participant.name}
          </span>
          {participant.role && (
            <span 
              className="text-xs px-1.5 py-0.5 rounded"
              style={{ background: COLORS.purple, color: 'white' }}
            >
              {participant.role}
            </span>
          )}
        </div>

        {/* Status Badge */}
        {StatusIcon && (
          <div 
            className="flex items-center gap-1 px-2 py-1 rounded-full text-xs"
            style={{ 
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(10px)',
              color: STATUS_OPTIONS.find(s => s.id === participant.status)?.color
            }}
          >
            <StatusIcon className="w-3 h-3" />
          </div>
        )}
      </div>

      {/* Speaking Time Indicator */}
      {speakingTime > 0 && (
        <div 
          className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full text-xs"
          style={{ 
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(10px)',
            color: COLORS.textMuted
          }}
        >
          <Clock className="w-3 h-3" />
          {Math.floor(speakingTime / 60)}m
        </div>
      )}

      {/* Muted indicator */}
      {!participant.audioEnabled && (
        <div 
          className="absolute top-3 left-3 w-7 h-7 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(239, 68, 68, 0.8)' }}
        >
          <MicOff className="w-3.5 h-3.5 text-white" />
        </div>
      )}
    </div>
  );
};

// ============================================
// CONTROL BUTTON COMPONENT
// ============================================
const ControlButton = ({ 
  active, 
  danger, 
  onClick, 
  icon: Icon, 
  label, 
  glowColor,
  disabled 
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`
      group relative flex flex-col items-center gap-1 transition-all duration-300
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
    title={label}
  >
    <div 
      className={`
        flex items-center justify-center rounded-full w-12 h-12 transition-all duration-300
        ${danger ? 'hover:scale-110' : 'hover:scale-105'}
      `}
      style={{ 
        background: danger 
          ? `linear-gradient(135deg, ${COLORS.danger} 0%, #DC2626 100%)`
          : active 
            ? COLORS.glass
            : 'rgba(255,255,255,0.05)',
        border: `1px solid ${danger ? 'transparent' : active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)'}`,
        backdropFilter: 'blur(10px)',
        boxShadow: active && glowColor ? `0 0 20px ${glowColor}` : danger ? `0 0 20px ${COLORS.dangerGlow}` : 'none'
      }}
    >
      <Icon className="w-5 h-5 text-white" />
    </div>
    <span className="text-[10px] text-white/60 font-medium">{label}</span>
  </button>
);

// ============================================
// MAIN VIDEO CALL COMPONENT
// ============================================
const PremiumVideoCall = ({ 
  threadId, 
  currentUser, 
  onClose,
  onPostSummary,
  initialRoomId = null 
}) => {
  // Core state
  const [roomId, setRoomId] = useState(initialRoomId);
  const [isConnecting, setIsConnecting] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [localStream, setLocalStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  
  // Media states
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  
  // UI states
  const [viewMode, setViewMode] = useState('floating'); // floating, fullscreen, pip
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [showTranscription, setShowTranscription] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [showBackgrounds, setShowBackgrounds] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showMediaDrop, setShowMediaDrop] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [myStatus, setMyStatus] = useState('none');
  const [callDuration, setCallDuration] = useState(0);
  const [activeReactions, setActiveReactions] = useState([]);
  const [transcripts, setTranscripts] = useState([]);
  const [sharedMedia, setSharedMedia] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [meetingSummary, setMeetingSummary] = useState(null);
  const [speakingTimes, setSpeakingTimes] = useState({});
  
  // PiP drag state
  const [pipPosition, setPipPosition] = useState({ x: window.innerWidth - 340, y: window.innerHeight - 260 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  
  // Refs
  const wsRef = useRef(null);
  const localVideoRef = useRef(null);
  const peerConnections = useRef({});
  const remoteStreams = useRef({});
  const mediaRecorder = useRef(null);
  const recordedChunks = useRef([]);
  const callStartTime = useRef(null);
  const containerRef = useRef(null);

  // Call duration timer
  useEffect(() => {
    let interval;
    if (isConnected && !callStartTime.current) {
      callStartTime.current = Date.now();
    }
    if (isConnected) {
      interval = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - callStartTime.current) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isConnected]);

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Initialize Call
  useEffect(() => {
    initializeCall();
    return () => cleanup();
  }, [threadId]);

  const initializeCall = async () => {
    try {
      setIsConnecting(true);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, facingMode: 'user' },
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      
      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      const token = localStorage.getItem('token');
      let currentRoomId = roomId;
      
      if (!currentRoomId) {
        const response = await fetch(`${API_BASE}/api/video/rooms?thread_id=${threadId}`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        currentRoomId = data.room_id;
        setRoomId(currentRoomId);
      }
      
      // Connect WebSocket
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = API_BASE.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      const wsUrl = `${wsProtocol}//${wsHost}/ws/video/${currentRoomId}?user_id=${currentUser.id}&user_name=${encodeURIComponent(currentUser.name)}&avatar=${encodeURIComponent(currentUser.avatar || currentUser.name?.slice(0,2) || '??')}`;
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        setIsConnected(true);
        setIsConnecting(false);
      };
      
      ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        handleSignalingMessage(message);
      };
      
      ws.onerror = () => toast.error('Connection error');
      ws.onclose = () => setIsConnected(false);
      
    } catch (error) {
      console.error('Failed to initialize call:', error);
      toast.error('Failed to access camera/microphone');
      setIsConnecting(false);
    }
  };

  // Signaling handler (simplified for brevity)
  const handleSignalingMessage = async (message) => {
    switch (message.type) {
      case 'room-joined':
        setParticipants(message.participants || []);
        break;
      case 'user-joined':
        setParticipants(message.participants || []);
        toast.success(`${message.user_name} joined`);
        break;
      case 'user-left':
        setParticipants(message.participants || []);
        break;
      case 'reaction':
        triggerReaction(message.emoji);
        break;
      case 'media-shared':
        setSharedMedia(prev => [...prev, message.media]);
        break;
      default:
        break;
    }
  };

  // Media Controls
  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  };

  const toggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
      }
    }
  };

  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      if (screenStream) {
        screenStream.getTracks().forEach(track => track.stop());
        setScreenStream(null);
      }
      setIsScreenSharing(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { cursor: 'always' },
          audio: true
        });
        setScreenStream(stream);
        setIsScreenSharing(true);
        stream.getVideoTracks()[0].onended = () => {
          setIsScreenSharing(false);
          setScreenStream(null);
        };
      } catch (error) {
        toast.error('Failed to share screen');
      }
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      if (mediaRecorder.current) {
        mediaRecorder.current.stop();
      }
      setIsRecording(false);
    } else {
      try {
        const stream = localStream;
        mediaRecorder.current = new MediaRecorder(stream, { mimeType: 'video/webm' });
        recordedChunks.current = [];
        
        mediaRecorder.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            recordedChunks.current.push(event.data);
          }
        };
        
        mediaRecorder.current.onstop = () => {
          const blob = new Blob(recordedChunks.current, { type: 'video/webm' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `recording-${new Date().toISOString()}.webm`;
          a.click();
          URL.revokeObjectURL(url);
        };
        
        mediaRecorder.current.start(1000);
        setIsRecording(true);
      } catch (error) {
        toast.error('Failed to start recording');
      }
    }
  };

  // Reactions
  const sendReaction = (emoji) => {
    triggerReaction(emoji);
    wsRef.current?.send(JSON.stringify({ type: 'reaction', emoji }));
    setShowReactions(false);
  };

  const triggerReaction = (emoji) => {
    const id = Date.now();
    setActiveReactions(prev => [...prev, { id, emoji }]);
  };

  const removeReaction = useCallback((id) => {
    setActiveReactions(prev => prev.filter(r => r.id !== id));
  }, []);

  // Transcript pin
  const togglePinTranscript = (idx) => {
    setTranscripts(prev => prev.map((t, i) => 
      i === idx ? { ...t, pinned: !t.pinned } : t
    ));
  };

  // PiP Drag handlers
  const handlePipMouseDown = (e) => {
    if (viewMode !== 'pip') return;
    setIsDragging(true);
    dragOffset.current = {
      x: e.clientX - pipPosition.x,
      y: e.clientY - pipPosition.y
    };
  };

  const handlePipMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setPipPosition({
      x: e.clientX - dragOffset.current.x,
      y: e.clientY - dragOffset.current.y
    });
  }, [isDragging]);

  const handlePipMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // Snap to corners
    const padding = 20;
    const w = 320;
    const h = 240;
    const { innerWidth, innerHeight } = window;
    
    let newX = pipPosition.x;
    let newY = pipPosition.y;
    
    // Horizontal snap
    if (pipPosition.x < innerWidth / 2 - w / 2) {
      newX = padding;
    } else {
      newX = innerWidth - w - padding;
    }
    
    // Vertical snap
    if (pipPosition.y < innerHeight / 2 - h / 2) {
      newY = padding;
    } else {
      newY = innerHeight - h - padding;
    }
    
    setPipPosition({ x: newX, y: newY });
  }, [isDragging, pipPosition]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handlePipMouseMove);
      window.addEventListener('mouseup', handlePipMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handlePipMouseMove);
      window.removeEventListener('mouseup', handlePipMouseUp);
    };
  }, [isDragging, handlePipMouseMove, handlePipMouseUp]);

  // Cleanup
  const cleanup = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
    }
    for (const pc of Object.values(peerConnections.current)) {
      pc.close();
    }
    peerConnections.current = {};
    if (wsRef.current) {
      wsRef.current.close();
    }
  };

  // End call with summary
  const endCall = async () => {
    // Generate AI summary
    const summary = {
      duration: formatDuration(callDuration),
      participants: participants.map(p => p.name),
      keyPoints: [
        'Discussed project timeline and milestones',
        'Reviewed design mockups for the new campaign',
        'Aligned on budget allocation for Q2'
      ],
      decisions: [
        'Approved the new brand color palette',
        'Agreed to launch MVP by end of month'
      ],
      actionItems: [
        { task: 'Finalize design assets by Friday', assignee: 'Design Team' },
        { task: 'Schedule follow-up with client', assignee: currentUser.name },
        { task: 'Update project roadmap in Notion', assignee: 'PM Team' }
      ]
    };
    
    setMeetingSummary(summary);
    setShowSummary(true);
  };

  const handlePostSummary = () => {
    if (onPostSummary && meetingSummary) {
      onPostSummary(meetingSummary);
    }
    cleanup();
    onClose();
  };

  const closeSummaryAndEnd = () => {
    cleanup();
    onClose();
  };

  // Container styles based on view mode
  const getContainerStyles = () => {
    if (viewMode === 'fullscreen') {
      return {
        position: 'fixed',
        inset: 0,
        zIndex: 100
      };
    }
    if (viewMode === 'pip') {
      return {
        position: 'fixed',
        left: pipPosition.x,
        top: pipPosition.y,
        width: 320,
        height: 240,
        zIndex: 9999,
        borderRadius: '20px',
        cursor: isDragging ? 'grabbing' : 'grab'
      };
    }
    // Floating
    return {
      position: 'fixed',
      bottom: 24,
      right: 24,
      width: 640,
      height: 480,
      zIndex: 100,
      borderRadius: '24px'
    };
  };

  // Grid layout based on participants
  const getGridLayout = () => {
    const count = participants.length + 1;
    if (count === 1) return 'grid-cols-1';
    if (count === 2) return 'grid-cols-2';
    if (count <= 4) return 'grid-cols-2';
    if (count <= 6) return 'grid-cols-3';
    return 'grid-cols-4';
  };

  return (
    <>
      <div 
        ref={containerRef}
        className="flex flex-col overflow-hidden"
        style={{ 
          ...getContainerStyles(),
          background: selectedBackground?.gradient || 'linear-gradient(135deg, #0F0F1A 0%, #1A1A2E 50%, #0F0F1A 100%)',
          boxShadow: '0 25px 100px -20px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255,255,255,0.05)'
        }}
        onMouseDown={viewMode === 'pip' ? handlePipMouseDown : undefined}
        data-testid="premium-video-call"
      >
        {/* Floating Reactions */}
        {activeReactions.map(r => (
          <FloatingReaction 
            key={r.id} 
            emoji={r.emoji} 
            onComplete={() => removeReaction(r.id)} 
          />
        ))}

        {/* Header */}
        {viewMode !== 'pip' && (
          <div 
            className="flex items-center justify-between px-4 py-3"
            style={{ 
              background: COLORS.glass,
              borderBottom: `1px solid ${COLORS.glassBorder}`,
              backdropFilter: 'blur(20px)'
            }}
          >
            <div className="flex items-center gap-3">
              <div className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-green-500' : 'bg-amber-500'} animate-pulse`} />
              <div>
                <span className="text-white text-sm font-medium">
                  {isConnecting ? 'Connecting...' : `${participants.length + 1} in call`}
                </span>
                {isConnected && (
                  <span className="text-white/40 text-xs ml-2 font-mono">
                    {formatDuration(callDuration)}
                  </span>
                )}
              </div>
              {isRecording && (
                <div 
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                  style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                >
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-red-400 text-xs font-semibold">REC</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('pip')}
                className="text-white/60 hover:text-white hover:bg-white/10 rounded-xl h-8 w-8 p-0"
              >
                <PictureInPicture2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode(viewMode === 'fullscreen' ? 'floating' : 'fullscreen')}
                className="text-white/60 hover:text-white hover:bg-white/10 rounded-xl h-8 w-8 p-0"
              >
                {viewMode === 'fullscreen' ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={endCall}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-xl h-8 w-8 p-0 ml-1"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Video Grid */}
        <div className="flex-1 relative overflow-hidden p-3">
          <div className={`h-full grid gap-3 ${getGridLayout()}`}>
            {/* Local video */}
            <ParticipantTile
              participant={{
                name: currentUser.name,
                avatar: currentUser.avatar || currentUser.name?.slice(0, 2),
                videoEnabled,
                audioEnabled,
                status: myStatus
              }}
              stream={localStream}
              isLocal
              isSpeaking={false}
              speakingTime={speakingTimes[currentUser.id] || 0}
              isLarge={participants.length === 1}
              avatarGradient={COLORS.avatarGradients[0]}
            />
            
            {/* Remote participants */}
            {participants.map((p, idx) => (
              <ParticipantTile
                key={p.socket_id || idx}
                participant={p}
                stream={remoteStreams.current[p.socket_id]}
                isLocal={false}
                isSpeaking={false}
                speakingTime={speakingTimes[p.user_id] || 0}
                avatarGradient={COLORS.avatarGradients[(idx + 1) % COLORS.avatarGradients.length]}
              />
            ))}
          </div>

          {/* Shared Media Overlay */}
          {sharedMedia.length > 0 && (
            <div className="absolute top-3 left-3 right-3 flex gap-2 overflow-x-auto pb-2">
              {sharedMedia.map((media, idx) => (
                <div 
                  key={idx}
                  className="flex-shrink-0 w-32 h-24 rounded-xl overflow-hidden"
                  style={{ background: COLORS.glass, border: `1px solid ${COLORS.glassBorder}` }}
                >
                  {media.type === 'image' ? (
                    <img src={media.url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <video src={media.url} className="w-full h-full object-cover" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Floating Toolbar */}
        {viewMode !== 'pip' && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
            <div 
              className="flex items-center gap-2 px-4 py-3 rounded-full"
              style={{ 
                background: COLORS.glass,
                backdropFilter: 'blur(30px)',
                border: `1px solid ${COLORS.glassBorder}`,
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
              }}
            >
              <ControlButton 
                active={audioEnabled} 
                onClick={toggleAudio} 
                icon={audioEnabled ? Mic : MicOff} 
                label="Mic"
                glowColor={audioEnabled ? COLORS.greenGlow : null}
              />
              <ControlButton 
                active={videoEnabled} 
                onClick={toggleVideo} 
                icon={videoEnabled ? Video : VideoOff} 
                label="Video"
                glowColor={videoEnabled ? COLORS.blueGlow : null}
              />
              <ControlButton 
                active={isScreenSharing} 
                onClick={toggleScreenShare} 
                icon={isScreenSharing ? MonitorOff : Monitor} 
                label="Share"
                glowColor={isScreenSharing ? COLORS.purpleGlow : null}
              />
              
              <div className="w-px h-8 bg-white/10 mx-1" />
              
              {/* Whiteboard */}
              <ControlButton 
                active={showWhiteboard} 
                onClick={() => setShowWhiteboard(!showWhiteboard)} 
                icon={PenTool} 
                label="Board"
              />
              
              {/* Reactions */}
              <div className="relative">
                <ControlButton 
                  active={showReactions} 
                  onClick={() => setShowReactions(!showReactions)} 
                  icon={Smile} 
                  label="React"
                />
                {showReactions && (
                  <div 
                    className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 flex gap-1 p-2 rounded-xl"
                    style={{ background: COLORS.glass, backdropFilter: 'blur(20px)', border: `1px solid ${COLORS.glassBorder}` }}
                  >
                    {REACTIONS.map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => sendReaction(emoji)}
                        className="w-10 h-10 rounded-lg hover:bg-white/10 flex items-center justify-center text-xl transition-transform hover:scale-110"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Backgrounds */}
              <div className="relative">
                <ControlButton 
                  active={showBackgrounds} 
                  onClick={() => setShowBackgrounds(!showBackgrounds)} 
                  icon={Palette} 
                  label="BG"
                />
                {showBackgrounds && (
                  <div 
                    className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 p-3 rounded-xl w-64"
                    style={{ background: COLORS.glass, backdropFilter: 'blur(20px)', border: `1px solid ${COLORS.glassBorder}` }}
                  >
                    <p className="text-white/60 text-xs mb-2">Backgrounds</p>
                    <div className="grid grid-cols-3 gap-2">
                      {BACKGROUND_PRESETS.map(bg => (
                        <button
                          key={bg.id}
                          onClick={() => {
                            setSelectedBackground(bg);
                            setShowBackgrounds(false);
                          }}
                          className={`h-12 rounded-lg transition-all ${selectedBackground?.id === bg.id ? 'ring-2 ring-purple-500' : ''}`}
                          style={{ background: bg.gradient || COLORS.surfaceLight }}
                        >
                          <span className="text-white/80 text-xs">{bg.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Transcription */}
              <ControlButton 
                active={showTranscription} 
                onClick={() => setShowTranscription(!showTranscription)} 
                icon={FileText} 
                label="Notes"
              />
              
              <div className="w-px h-8 bg-white/10 mx-1" />
              
              {/* Recording */}
              <ControlButton 
                active={isRecording} 
                onClick={toggleRecording} 
                icon={isRecording ? Pause : Play} 
                label={isRecording ? 'Stop' : 'Rec'}
                glowColor={isRecording ? COLORS.dangerGlow : null}
              />
              
              {/* End Call */}
              <ControlButton 
                danger 
                onClick={endCall} 
                icon={PhoneOff} 
                label="End"
              />
            </div>
          </div>
        )}

        {/* Mini PiP Controls */}
        {viewMode === 'pip' && (
          <div 
            className="absolute bottom-0 inset-x-0 p-2 flex items-center justify-center gap-1"
            style={{ 
              background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, transparent 100%)',
              pointerEvents: 'auto'
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              onClick={toggleAudio}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all"
              style={{ 
                background: audioEnabled ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                border: `1px solid ${audioEnabled ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)'}`
              }}
            >
              {audioEnabled ? <Mic className="w-3.5 h-3.5 text-white" /> : <MicOff className="w-3.5 h-3.5 text-white" />}
            </button>
            <button
              onClick={toggleVideo}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all"
              style={{ 
                background: videoEnabled ? 'rgba(59, 130, 246, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                border: `1px solid ${videoEnabled ? 'rgba(59, 130, 246, 0.5)' : 'rgba(239, 68, 68, 0.5)'}`
              }}
            >
              {videoEnabled ? <Video className="w-3.5 h-3.5 text-white" /> : <VideoOff className="w-3.5 h-3.5 text-white" />}
            </button>
            <button
              onClick={() => setViewMode('floating')}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:bg-white/20"
              style={{ background: COLORS.glass }}
            >
              <Maximize2 className="w-3.5 h-3.5 text-white" />
            </button>
            <button
              onClick={endCall}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all"
              style={{ background: 'rgba(239, 68, 68, 0.8)' }}
            >
              <PhoneOff className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
        )}

        {/* Whiteboard Overlay */}
        {showWhiteboard && <Whiteboard onClose={() => setShowWhiteboard(false)} />}

        {/* Transcription Panel */}
        {showTranscription && viewMode !== 'pip' && (
          <TranscriptionPanel 
            transcripts={transcripts}
            onPin={togglePinTranscript}
            onClose={() => setShowTranscription(false)}
          />
        )}

        {/* Connecting Overlay */}
        {isConnecting && (
          <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ background: 'rgba(15, 15, 26, 0.95)' }}>
            <div className="relative">
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.pink} 100%)` }}
              >
                <Loader2 className="w-10 h-10 text-white animate-spin" />
              </div>
              <div 
                className="absolute inset-0 rounded-full animate-ping opacity-30"
                style={{ background: COLORS.purple }}
              />
            </div>
            <p className="text-white font-medium mt-6">Joining the call...</p>
            <p className="text-white/50 text-sm mt-1">Setting up your audio and video</p>
          </div>
        )}
      </div>

      {/* Meeting Summary Modal */}
      {showSummary && (
        <MeetingSummaryModal 
          summary={meetingSummary}
          onClose={closeSummaryAndEnd}
          onPostToThread={handlePostSummary}
        />
      )}
    </>
  );
};

export default PremiumVideoCall;
